/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { useMutation } from '@apollo/client';
import { AiCoach_AsyncTaskStatus as AsyncTaskStatus } from '__generated__/graphql-types';

import { Link, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachButton } from 'bundles/ai-coach-platform/components/building-blocks';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import useCoachAsyncTask from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useCoachAsyncTask';
import { CoachSettingsContext } from 'bundles/ai-coach-platform/components/patterns/chat/settings/SettingsContextProvider';
import SettingsSwitch from 'bundles/ai-coach-platform/components/patterns/chat/settings/SettingsSwitch';
import DeleteAllMemoryModal from 'bundles/ai-coach-platform/components/patterns/chat/settings/pages/memory/DeleteAllMemoryModal';
import MemoryList from 'bundles/ai-coach-platform/components/patterns/chat/settings/pages/memory/MemoryList';
import OptOutModal from 'bundles/ai-coach-platform/components/patterns/chat/settings/pages/memory/OptOutModal';
import { SettingsView } from 'bundles/ai-coach-platform/components/patterns/chat/settings/types';
import useCoachMemory, {
  clearPendingTask,
  getPendingTask,
  setPendingTask,
} from 'bundles/ai-coach-platform/components/patterns/chat/settings/useCoachMemory';
import { useSettingsNotification } from 'bundles/ai-coach-platform/components/patterns/chat/settings/useSettingsNotification';
import type {
  AiCoachPlatform_SaveCoachUserSettingsMutation as SaveCoachUserSettingsMutation,
  AiCoachPlatform_SaveCoachUserSettingsMutationVariables as SaveCoachUserSettingsMutationVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachSaveUserSettingsMutation';
import aiCoachSaveUserSettingsMutation from 'bundles/ai-coach-platform/queries/aiCoachSaveUserSettingsMutation.graphql';
import { getFeatureNameLabels } from 'bundles/ai-coach-platform/utils/brandLabels';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';
import { buildLearnerHelpUrl } from 'bundles/common/utils/urlUtils';
import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/ai-coach-platform';

import SettingsPage from '../../SettingsPage';

const styles = {
  root: css`
    height: 100%;
  `,

  memoryItemsContainer: css`
    width: 100%;
    height: auto;
  `,

  footer: css`
    display: flex;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-100) 0;
  `,
};

type Props = {
  onBack: () => void;
};

export const SettingsMemory = (props: Props): React.ReactElement => {
  const { onBack } = props;
  const track = useTracker();
  const trackingData = useCoachTrackingContext()?.data;
  const { updateData, data, brandLabel = 'Coach' } = useContext(CoachSettingsContext);
  const { isBrandUpdateEnabled } = useCoachChatContext();
  const { featureNameLabel, featureNameLabelLower } = getFeatureNameLabels(isBrandUpdateEnabled, brandLabel);
  const { addNotification, clearNotification } = useSettingsNotification();
  const {
    userMemories,
    loading,
    deleteMemories,
    fetchMemories,
    fetchMoreMemories,
    total: totalMemoryCount,
  } = useCoachMemory();
  const { fetchTaskStatus, taskStatus, stopPolling } = useCoachAsyncTask({});
  const memoriesEnabled = data.memory?.enabled;

  const [toggle, setToggle] = useState<boolean>(!!memoriesEnabled);
  const [memoryEnabled, setMemoryEnabled] = useState<boolean>(!!memoriesEnabled);
  const [selectedMemoryIds, setSelectedMemoryIds] = useState<string[]>([]);
  const [isAllMemorySelected, setIsAllMemorySelected] = useState<boolean>(false);
  const [isDeletionInProgress, setIsDeletionInProgress] = useState<boolean>(false);
  const [loadingMore, setIsLoadingMore] = useState(false);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState<boolean>(false);
  const [isOptOutModalOpen, setIsOptOutModalOpen] = useState<boolean>(false);
  const pendingCompleteRef = React.useRef<null | (() => void)>(null);
  const deleteButtonRef = React.useRef<HTMLButtonElement>(null);
  const { formatMessage } = useIntl();
  const isFirstFetchRef = React.useRef<boolean>(false);
  const memoriesShownCountRef = React.useRef<number>(userMemories.length);
  const totalCountRef = React.useRef<number>(totalMemoryCount);
  const [saveSettings, { loading: isSaving }] = useMutation<
    SaveCoachUserSettingsMutation,
    SaveCoachUserSettingsMutationVariables
  >(aiCoachSaveUserSettingsMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const isDeleteButtonDisabled = isDeletionInProgress || !(selectedMemoryIds.length > 0);
  const isDeleteAllButtonDisabled = isDeletionInProgress || !isAllMemorySelected;

  const resetDeletionStates = () => {
    stopPolling();
    setIsDeletionInProgress(false);
    setIsAllMemorySelected(false);
    setSelectedMemoryIds([]);
  };

  const handleFetchMemories = React.useCallback<() => void>(() => {
    setIsLoadingMore(true);
    // no need to fetch memories if we're showing the "deletion in progress" state
    fetchMemories().then(() => {
      setIsLoadingMore(false);
    });
  }, [fetchMemories]);

  // use this effect to do things only once on mount
  React.useEffect(() => {
    // check if there is a previous pending task from localstorage on mount
    const pendingTaskId: string | null = getPendingTask();

    if (pendingTaskId) {
      fetchTaskStatus(pendingTaskId);
    } else {
      isFirstFetchRef.current = true;
      handleFetchMemories();
    }

    track('view_coach_settings', {
      pageType: 'memory',
      coach: {
        mode: trackingData?.mode,
      },
    });

    return () => {
      clearNotification();
      stopPolling();
    };
  }, []); // eslint-disable-line

  // track ref changes when memory counts update (memories fetched or deleted)
  React.useEffect(() => {
    memoriesShownCountRef.current = userMemories.length;
    totalCountRef.current = totalMemoryCount;
  }, [userMemories.length, totalMemoryCount]);

  // effect to handle taskStatus changes
  React.useEffect(() => {
    if (taskStatus === AsyncTaskStatus.Completed) {
      if (pendingCompleteRef.current) {
        pendingCompleteRef.current();
        pendingCompleteRef.current = null;
      }

      resetDeletionStates();
      clearPendingTask(); // remove from localstorage

      clearNotification();
      addNotification({
        content: formatMessage(_t('{count, plural, =1 {Memory} other {Memories}} deleted.'), {
          count: selectedMemoryIds.length,
        }),
        severity: 'success',
        isTransient: true,
      });

      const hasMoreMemoriesToLoad = userMemories.length === 0 && totalMemoryCount > 0;

      if (isFirstFetchRef.current === false || hasMoreMemoriesToLoad) {
        // fetch any new memories after a pending deletion task was completed after mount
        // since no memories would be fetched otherwise
        // OR if there are more memories to load after a "Select all + delete"
        handleFetchMemories();
      }
    } else if (taskStatus === AsyncTaskStatus.InProgress || taskStatus === AsyncTaskStatus.Pending) {
      setIsDeletionInProgress(true);
      addNotification({
        content: _t('Memory deletion in progress'),
        severity: 'information',
      });
    } else if (taskStatus === AsyncTaskStatus.Failed) {
      addNotification({ content: _t('Something went wrong with deletion, please try again'), severity: 'error' });
    }
  }, [taskStatus]); // eslint-disable-line

  const saveMemoryPreference = ({ enableMemory = false, onSuccess = () => {}, onError = () => {} }) => {
    saveSettings({
      variables: {
        input: {
          memoryPreferenceInput: {
            enableMemory,
          },
        },
      },
    })
      .then(() => {
        onSuccess();
        updateData({ ...data, [SettingsView.MEMORY]: { enabled: enableMemory } });
        addNotification({
          content: enableMemory
            ? _t(`You've opted-in to Memory. #{featureNameLabel} will now save relevant information from your chats.`, {
                featureNameLabel,
              })
            : _t(
                `You've opted-out of Memory. #{featureNameLabel} will no longer save relevant information from your chats.`,
                {
                  featureNameLabel,
                }
              ),
          severity: 'success',
          isTransient: true,
        });
      })
      .catch((error) => {
        onError();
        addNotification({ content: _t('Something went wrong, please try again'), severity: 'error' });
        captureMessageInSentry({ error, source: 'SaveMemoryPreferences' });
      });
  };

  const handleToggle = (newSelected: boolean) => {
    // optimistically update toggle, revert on error
    setToggle(newSelected);

    // Clear any selected memories
    if (selectedMemoryIds && !newSelected) {
      setSelectedMemoryIds([]);
    }

    if (isAllMemorySelected && !newSelected) {
      setIsAllMemorySelected(false);
    }

    track('interact_coach_settings', {
      settingsInteractionType: `toggle_memory_${newSelected ? 'on' : 'off'}`,
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'settings',
      settingsInteractionType: `toggle_memory_${newSelected ? 'on' : 'off'}`,
    });

    saveMemoryPreference({
      enableMemory: newSelected,
      onSuccess: () => setMemoryEnabled(newSelected),
      onError: () => setToggle(!newSelected),
    });
  };

  // TODO: combine this and `handleDeleteMemory` into one function to avoid dupe code?
  const handleDeleteAllMemory = async () => {
    setIsDeletionInProgress(true);

    addNotification({
      content: _t('All memory deletion in progress'),
      severity: 'information',
    });

    track('interact_coach_settings', {
      settingsInteractionType: 'deleteall_memory',
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'settings',
      settingsInteractionType: 'deleteall_memory',
    });

    const { taskId, errors, onTaskComplete } = await deleteMemories([], true);

    if (errors) {
      resetDeletionStates();
      addNotification({
        content: _t('An error occurred. Please try again.'),
        severity: 'error',
      });
      if (errors[0]) {
        captureMessageInSentry({ error: errors[0], source: 'DeleteAllMemory' });
      }
      return;
    }

    if (taskId) {
      const status = await fetchTaskStatus(taskId);

      if (!status) {
        return;
      }

      if (status === AsyncTaskStatus.Failed) {
        resetDeletionStates();
        addNotification({
          content: _t('An error occurred. Please try again.'),
          severity: 'error',
        });

        captureMessageInSentry({ error: new Error(`AsyncTask failed, ${taskId}`), source: 'AsyncTaskStatus' });
        return;
      }

      if (status === AsyncTaskStatus.Completed) {
        onTaskComplete();
        resetDeletionStates();
        addNotification({
          content: _t('All memories deleted.'),
          severity: 'success',
          isTransient: true,
        });
      } else if (status === AsyncTaskStatus.InProgress || status === AsyncTaskStatus.Pending) {
        // persist in localstorage to check on next mount
        setPendingTask(taskId);
      }
    }
  };

  const handleDeleteMemory = async () => {
    // Mock delete behavior for now and show skeleton while deletion is in progress
    setIsDeletionInProgress(true);

    track('interact_coach_settings', {
      settingsInteractionType: 'delete_memory',
      settingsMetadata: {
        ids: selectedMemoryIds,
      },
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'settings',
      settingsInteractionType: 'delete_memory',
      settingsMetadata: {
        ids: selectedMemoryIds,
      },
    });

    addNotification({
      content: _t('Memory deletion in progress'),
      severity: 'information',
    });

    const { taskId, errors, onTaskComplete } = await deleteMemories(selectedMemoryIds);

    if (errors) {
      resetDeletionStates();
      addNotification({
        content: _t('An error occurred. Please try again.'),
        severity: 'error',
      });
      if (errors[0]) {
        captureMessageInSentry({ error: errors[0], source: 'DeleteMemory' });
      }
      return;
    }

    if (taskId) {
      const status = await fetchTaskStatus(taskId);

      if (!status) {
        return;
      }

      if (status === AsyncTaskStatus.Failed) {
        resetDeletionStates();
        addNotification({
          content: _t('An error occurred. Please try again.'),
          severity: 'error',
        });

        captureMessageInSentry({ error: new Error(`AsyncTask failed, ${taskId}`), source: 'AsyncTaskStatus' });
        return;
      }

      if (status === AsyncTaskStatus.Completed) {
        onTaskComplete();
        resetDeletionStates();
        addNotification({
          content: _t('Memories deleted.'),
          severity: 'success',
          isTransient: true,
        });
      } else if (status === AsyncTaskStatus.InProgress || status === AsyncTaskStatus.Pending) {
        // persist in localstorage to check on next mount
        setPendingTask(taskId);

        // save `onTaskComplete` for future async execution
        pendingCompleteRef.current = onTaskComplete;
      }
    }
  };

  const handleMemoryOpt = (enabled = false) => {
    if (enabled) {
      // don't show any confirmation modal when opting in
      handleToggle(true);
      return;
    }

    // only show the Opt out modal if there are existing memories
    if (totalMemoryCount === 0) {
      handleToggle(false);
    } else {
      setToggle(false);
      setIsOptOutModalOpen(true);
    }
  };
  const handleOptOutConfirm = () => {
    setIsOptOutModalOpen(false);
    handleToggle(false);
    setMemoryEnabled(false);
  };
  const handleOptOutModalCancel = () => {
    setToggle(true);
    setIsOptOutModalOpen(false);
    setMemoryEnabled(true);
  };

  const handleFetchMoreMemories = async () => {
    const shouldFetchMore = memoriesShownCountRef.current < totalCountRef.current;

    if (shouldFetchMore && fetchMoreMemories) {
      setIsLoadingMore(true);

      const result = await fetchMoreMemories();
      setIsLoadingMore(false);

      if (result?.error) {
        addNotification({
          content: _t('An error occurred. Please try again.'),
          severity: 'error',
          isTransient: false,
        });
      }
    }
  };

  return (
    <div className="coach-settings-memory" css={styles.root}>
      <SettingsPage
        onBack={onBack}
        title={_t('Memory')}
        footer={
          <div css={styles.footer}>
            <CoachButton
              disabled={isDeleteButtonDisabled}
              aria-disabled={isDeleteButtonDisabled}
              onClick={handleDeleteMemory}
              ref={deleteButtonRef}
            >
              {selectedMemoryIds.length > 0
                ? _t('Delete (#{count})', { count: selectedMemoryIds.length })
                : _t('Delete')}
            </CoachButton>
            {/* only show "Delete all" if we're not showing all the memories */}
            {isAllMemorySelected && userMemories.length < totalMemoryCount && (
              <CoachButton
                disabled={isDeleteAllButtonDisabled}
                aria-disabled={isDeleteAllButtonDisabled}
                onClick={() => {
                  setIsDeleteAllModalOpen(true);
                }}
                variant="ghost"
              >
                {_t('Delete all (#{count})', { count: totalMemoryCount })}
              </CoachButton>
            )}
          </div>
        }
      >
        <SettingsSwitch
          disabled={isSaving}
          label={_t('Memory')}
          supportText={
            <Typography2 variant="bodySecondary" component="div" color="supportText">
              <Typography2 variant="bodySecondary" component="p" color="supportText">
                {_t(
                  `#{featureNameLabel} will remember helpful context from your chats - like your goals, interests, learning style, or how you prefer #{featureNameLabelLower} to respond (e.g. use bullets) - to personalize the experience.`,
                  { featureNameLabel, featureNameLabelLower }
                )}
                &nbsp;
                <Link
                  href={buildLearnerHelpUrl('000002224-Advance-your-learning-with-Coursera-Coach')}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={_t('Learn more about memory')}
                >
                  {_t('Learn more')}
                </Link>
              </Typography2>
            </Typography2>
          }
          selected={toggle}
          onToggle={handleMemoryOpt}
        />

        <Typography2 variant="bodySecondary" component="p" color="supportText">
          {_t(
            'You can delete any memories #{featureNameLabelLower} has collected below that you feel are not relevant or accurate.',
            {
              featureNameLabelLower,
            }
          )}
        </Typography2>

        <div className="memory-items-container" css={styles.memoryItemsContainer}>
          <MemoryList
            isAllMemorySelected={isAllMemorySelected}
            totalMemoryCount={totalMemoryCount}
            isDeleteButtonDisabled={isDeleteButtonDisabled}
            isDeleting={isDeletionInProgress}
            isLoadingMoreItems={loading || loadingMore}
            selectedMemories={selectedMemoryIds}
            memories={userMemories}
            setSelectedMemories={setSelectedMemoryIds}
            setIsAllMemorySelected={setIsAllMemorySelected}
            onFetchMoreMemories={handleFetchMoreMemories}
            deleteButtonRef={deleteButtonRef}
            memoryEnabled={memoryEnabled}
          />
        </div>

        {isDeleteAllModalOpen && (
          <DeleteAllMemoryModal
            isOpen={isDeleteAllModalOpen}
            onClose={() => setIsDeleteAllModalOpen(false)}
            onSubmit={handleDeleteAllMemory}
            brandLabel={brandLabel}
          />
        )}

        {isOptOutModalOpen && (
          <OptOutModal
            isOpen={isOptOutModalOpen}
            onSubmit={handleOptOutConfirm}
            onCancel={handleOptOutModalCancel}
            brandLabel={brandLabel}
          />
        )}
      </SettingsPage>
    </div>
  );
};

export default SettingsMemory;
